import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import { Analytics } from "./types";

export class CustomerioSourceProvider {
  initialize(customerioClient: CustomerioClient) {
    const cioanalytics = AnalyticsBrowser.load({
      writeKey: customerioClient.writeKey,
    });

    return CustomerioAnalytics(cioanalytics);
  }
}

interface CustomerioTrackerOptions {
  writeKey: string;
}

export interface CustomerioClient extends CustomerioTrackerOptions {
  provider: "customerio";
}

export function makeCustomerioClient(
  tracker: CustomerioTrackerOptions
): CustomerioClient {
  return {
    provider: "customerio",
    writeKey: tracker.writeKey,
  };
}

/**
 * Customerio will only record where the source is WL. For events on FM, we don't include customerio in `allowedProviders` and for Portal
 * we exclude events with `source=Portal`
 */
function CustomerioAnalytics(sdk: AnalyticsBrowser): Analytics {
  let registeredProperties: { [key: string]: string } = {};

  return {
    group: () => {},
    register: (properties) => {
      Object.keys(properties).forEach((key) => {
        registeredProperties[key] = properties[key];
      });
    },

    // IMPORTANT
    // https://docs.customer.io/integrations/data-in/connections/javascript/js-source/#auto-identify
    identify: (userID, properties) => {
      if (userID) {
        sdk.identify(userID, {
          partner_id: registeredProperties["Partner ID"],
          partner_name: registeredProperties["Partner Name"],
          partner_internal_name: registeredProperties["Partner Internal Name"],
          properties,
        });
      } else if (properties?.email) {
        sdk.identify(properties?.email, {
          partner_id: registeredProperties["Partner ID"],
          partner_name: registeredProperties["Partner Name"],
          partner_internal_name: registeredProperties["Partner Internal Name"],
          properties,
        });
      }

      sdk.track("Identify Subscription", {
        partner_id: registeredProperties["Partner ID"],
        partner_name: registeredProperties["Partner Name"],
        partner_internal_name: registeredProperties["Partner Internal Name"],
      });
    },
    screen: (name, properties = {}) => {
      const {
        partnerId,
        partnerName,
        partnerInternalName,
        pageName,
        template,
        catalogSlug,
        ...mainProperties
      } = properties;

      if (
        properties.Source === "Portal" ||
        registeredProperties.Source === "Portal"
      ) {
        return;
      }

      if (!partnerId || !partnerName || !pageName) {
        return;
      }

      const partnerProperties = {
        "Partner ID": partnerId,
        "Partner Name": partnerName,
        "Partner Internal Name": partnerInternalName || partnerName,
        "Page Name": pageName,
      };

      registeredProperties = {
        ...registeredProperties,
        ...partnerProperties,
        ...(catalogSlug && { "Catalog Slug": catalogSlug }),
      };

      sdk.page("Page View", name, {
        ...registeredProperties,
        ...mainProperties,
        ...partnerProperties,
        Name: name,
        "Page URL": window.location.href,
        referrer: document.referrer,
      });
    },
    ecommerceEvent: (event) => {
      const { event: eventName, ...properties } = event;
      sdk.track(eventName, {
        ...registeredProperties,
        ...properties,
      });
    },
    event: (eventName, properties) => {
      if (
        properties?.Source === "Portal" ||
        registeredProperties.Source === "Portal"
      ) {
        return;
      }

      sdk.track(eventName, {
        ...registeredProperties,
        ...properties,
      });
    },
  };
}
